import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import BasketballCourt from "@/components/BasketballCourt";
import { SHOT_TYPE_MAP } from "@/lib/zones";

export default function CoachDashboard() {
  const [teamStats, setTeamStats] = useState(null);
  const [ranking, setRanking] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [t, r] = await Promise.all([
          api.get("/stats/team"),
          api.get("/stats/ranking"),
        ]);
        setTeamStats(t.data);
        setRanking(r.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <Layout><div className="text-zinc-400">Carregando...</div></Layout>;

  const stats = teamStats || { total_made: 0, total_attempts: 0, pct: 0, by_zone: {}, by_type: {} };

  return (
    <Layout>
      <div className="space-y-8" data-testid="coach-dashboard">
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-zinc-500">Dashboard do Técnico</div>
            <h1 className="font-display font-black text-5xl sm:text-6xl uppercase tracking-tight mt-1">Visão da Equipe</h1>
          </div>
        </header>

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi label="Aproveitamento" value={`${stats.pct}%`} accent="#FF3B30" testId="kpi-pct" />
          <Kpi label="Acertos" value={stats.total_made} testId="kpi-made" />
          <Kpi label="Arremessos" value={stats.total_attempts} testId="kpi-attempts" />
          <Kpi label="Atletas Ativos" value={ranking.filter(r => r.attempts > 0).length} testId="kpi-active-athletes" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Court */}
          <div className="lg:col-span-2 bg-[#141414] border border-zinc-800 rounded-md p-6">
            <h2 className="font-display font-bold text-2xl uppercase tracking-tight mb-4">Mapa de Calor Geral</h2>
            <BasketballCourt zoneStats={stats.by_zone} />
          </div>

          {/* Ranking */}
          <div className="bg-[#141414] border border-zinc-800 rounded-md p-6">
            <h2 className="font-display font-bold text-2xl uppercase tracking-tight mb-4">Ranking</h2>
            <div className="space-y-2" data-testid="ranking-list">
              {ranking.length === 0 && <p className="text-sm text-zinc-500">Nenhuma atleta cadastrada.</p>}
              {ranking.map((a, idx) => (
                <button
                  key={a.athlete_id}
                  onClick={() => navigate(`/atleta/${a.athlete_id}`)}
                  data-testid={`rank-${a.athlete_id}`}
                  className="w-full flex items-center gap-3 p-3 bg-[#0A0A0A] border border-zinc-800 rounded-sm hover:border-zinc-600 transition-all text-left"
                >
                  <span className="font-display font-black text-2xl text-zinc-600 w-6">{idx + 1}</span>
                  {a.picture ? (
                    <img src={a.picture} alt="" className="w-9 h-9 rounded-full object-cover" referrerPolicy="no-referrer" />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center font-bold text-sm">
                      {a.name?.[0]?.toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-bold truncate">{a.name}</div>
                    <div className="text-xs text-zinc-500">{a.attempts} arrem. • {a.made} acertos</div>
                  </div>
                  <div className="text-right">
                    <div className="font-display font-black text-2xl">{a.pct}%</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Shot type breakdown */}
        <div className="bg-[#141414] border border-zinc-800 rounded-md p-6">
          <h2 className="font-display font-bold text-2xl uppercase tracking-tight mb-4">Por Tipo de Arremesso</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
            {Object.entries(stats.by_type || {}).map(([k, v]) => (
              <div key={k} className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-4">
                <div className="text-xs uppercase tracking-wider text-zinc-500 font-bold">{SHOT_TYPE_MAP[k] || k}</div>
                <div className="font-display font-black text-3xl mt-2">{v.attempts > 0 ? `${v.pct}%` : "—"}</div>
                <div className="text-xs text-zinc-500 mt-1">{v.made}/{v.attempts}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
}

function Kpi({ label, value, accent, testId }) {
  return (
    <div className="bg-[#141414] border border-zinc-800 rounded-md p-5 relative overflow-hidden" data-testid={testId}>
      {accent && <div className="absolute top-0 left-0 w-full h-0.5" style={{ background: accent }} />}
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <div className="font-display font-black text-4xl sm:text-5xl mt-2 tracking-tighter">{value}</div>
    </div>
  );
}
